import { computed, type Ref } from 'vue'

export const SPECIAL_STEP_IDS = new Set([
  'metrics-merge',
  'record-extraction-assembly',
  'final-output',
])
export const ANSWER_COLLECTION_ID = 'answer-collection'

export function isSpecialStep(stepId: string): boolean {
  return SPECIAL_STEP_IDS.has(stepId)
}

export interface StepTask {
  stepId: string
  name: string
  statusName: string
  status: string
  statusType: 'running' | 'done' | 'pending'
  current: number
  total: number
}

/**
 * 对特殊步骤做后处理：total 取自 answer-collection 的 progressTotal，
 * current 根据 status 决定 0 或 total（二进制进度）。
 */
export function applySpecialStepOverrides(taskList: StepTask[]): StepTask[] {
  const answerStep = taskList.find(t => t.stepId === ANSWER_COLLECTION_ID)
  const answerTotal = answerStep?.total || 0

  return taskList.map(task => {
    if (!SPECIAL_STEP_IDS.has(task.stepId)) return task
    return {
      ...task,
      total: answerTotal,
      current: task.statusType === 'done' ? answerTotal : 0,
    }
  })
}

/**
 * 获取当前选中步骤实际应请求的问题 stepId：
 * 特殊步骤降级为 answer-collection
 */
export function getEffectiveQuestionStepId(selectItem: StepTask | null): string {
  if (!selectItem) return ''
  return SPECIAL_STEP_IDS.has(selectItem.stepId) ? ANSWER_COLLECTION_ID : selectItem.stepId
}

/**
 * 特殊步骤下的问题状态由步骤自身的 statusType 统一决定
 */
export function mapSpecialStepQuestionStatus(
  questions: Array<{ questionText: string; platformKey: string; status: string }>,
  stepStatusType: 'running' | 'done' | 'pending',
): Array<{ questionText: string; platformKey: string; status: string }> {
  const mappedStatus =
    stepStatusType === 'done' ? 'completed' : stepStatusType === 'running' ? 'running' : 'pending'
  return questions.map(q => ({ ...q, status: mappedStatus }))
}

export function useSpecialStepHandler(taskList: Ref<StepTask[]>) {
  const answerCollectionStep = computed(() =>
    taskList.value.find(t => t.stepId === ANSWER_COLLECTION_ID),
  )

  const totalQuestions = computed(() => answerCollectionStep.value?.total || 0)

  return {
    answerCollectionStep,
    totalQuestions,
    isSpecialStep,
    applySpecialStepOverrides,
    getEffectiveQuestionStepId,
    mapSpecialStepQuestionStatus,
  }
}
